/**
 * 서버 wire 계약 — zep-quiz-ai docs/architecture/similar-question-option-fill-api.md.
 * snake_case 그대로 둔다. DTO 타입은 전부 이 스키마의 z.infer 다.
 *
 * 입구(요청) 관대 · 출구(응답) 엄격 — 응답은 런타임에 검증하고, 계약을 어기면
 * QuestionCardAiInvalidResponseError(error_code `INVALID_RESPONSE`)로 실패시킨다.
 * 응답에서 검증하는 필드는 FE 가 실제로 읽는 것뿐이다. 읽지 않는 필드(brainstorm ·
 * latency_ms · trace_id)까지 필수로 두면 무해한 계약 변경이 사용자 실패가 된다.
 */
import { z } from 'zod';

import { QuizBaseSchema } from '../../../form/schema/quiz/QuizBaseSchema';
import { toQuizPlainText } from '../../ai-builder-chat/utils/adaptAiGeneratedQuizzesToBuildForm';
import { type QUESTION_CARD_AI_QUIZ_TYPES } from '../consts/questionCardAi.const';
import { type BuilderContextInput } from '../types/questionCardAi.types';

import {
  postQuestionCardAiJson,
  type QuestionCardAiTransportDependencies,
} from './questionCardAiHttpClient';

const CurriculumRefSchema = z.object({
  standard_code: z.string(),
  subject: z.string(),
});

/** 입구 관대 — 빈 선지·빈 답 허용. 알 수 없는 키는 서버가 그대로 되돌린다. */
const SourceQuestionSchema = z
  .object({
    quiz_type: z.string(),
    question: z.string(),
    options: z.array(z.string()).optional(),
    answer: z.union([z.array(z.string()), z.string()]).optional(),
    comment: z.string().optional(),
    difficulty: z.number().nullish(),
    curriculum_list: z.array(CurriculumRefSchema).optional(),
  })
  .passthrough();

/**
 * 폼 저장 규칙(QuizBaseSchema.question — 1~300자)과 공백 전용 금지를 어댑터와 같은 평문 정규화
 * 뒤에 적용한다. 통과 못 하면 삽입돼도 저장할 수 없는 문항이 성공으로 기록된다.
 */
const SavableQuestionSchema = z.string().refine(text => {
  const plain = toQuizPlainText(text);
  return (
    !!plain.trim() && QuizBaseSchema.shape.question.safeParse(plain).success
  );
}, 'unsavable question');

const WireQuestionBaseSchema = z
  .object({
    question: SavableQuestionSchema,
    comment: z.string().nullish(),
    difficulty: z.number().nullish(),
    curriculum_list: z.array(CurriculumRefSchema).nullish(),
  })
  .passthrough();

/** 어댑터와 같은 평문 정규화 뒤에도 내용이 남아야 한다 — `<strong></strong>` 같은 마크만 있는 값은 빈 칸이 된다. */
const NonBlankSchema = z
  .string()
  .refine(text => !!toQuizPlainText(text).trim(), 'blank');
const OxAnswerSchema = z.enum(['o', 'x']);

/**
 * 출구 엄격 — 서버 QuestionPayload 를 통과한 문항. 그대로 폼 문항으로 삽입되므로 **유형별로
 * 저장 가능한 정답**을 요구한다. 정답이 비면 어댑터가 ox 정답 undefined · 정답 없는 객관식 ·
 * 빈 단답을 만들어, 사용자는 저장할 수 없는 문항을 성공으로 받는다.
 */
const WireQuestionSchema = z
  .discriminatedUnion('quiz_type', [
    WireQuestionBaseSchema.extend({
      quiz_type: z.literal('ox'),
      options: z.array(z.string()).nullish(),
      // 계약은 'o'|'x' 스칼라. 어댑터가 배열도 받으므로 한 칸짜리 배열도 허용한다.
      answer: z.union([OxAnswerSchema, z.array(OxAnswerSchema).length(1)]),
    }),
    WireQuestionBaseSchema.extend({
      quiz_type: z.literal('multiple'),
      options: z.array(NonBlankSchema).min(2),
      /**
       * 정답 선지의 index 문자열. 어댑터가 `String(index)` 와 문자열로 비교하므로 '01' 같은
       * 선행 0 은 거부한다. 범위는 아래 superRefine 이 본다.
       */
      answer: z.array(z.string().regex(/^(0|[1-9]\d*)$/)).min(1),
    }),
    WireQuestionBaseSchema.extend({
      quiz_type: z.literal('subjective'),
      options: z.array(z.string()).nullish(),
      answer: z.array(NonBlankSchema).min(1),
    }),
    WireQuestionBaseSchema.extend({
      quiz_type: z.literal('essay'),
      options: z.array(z.string()).nullish(),
      answer: z.union([z.array(z.string()), z.string()]).nullish(),
    }),
  ])
  .superRefine((question, ctx) => {
    if (
      question.quiz_type === 'multiple' &&
      question.answer.some(index => Number(index) >= question.options.length)
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['answer'],
        message: 'answer index out of options',
      });
  });

const BuilderContextSchema = z.object({
  school_level: z.string(),
  grade: z.string(),
  subject: z.string(),
});

const SimilarQuestionVariantSchema = z.enum(['similar', 'harder']);

const CommonRequestSchema = z.object({
  locale: z.string(),
  question: SourceQuestionSchema,
  builder_context: BuilderContextSchema.optional(),
  client_request_id: z.string().optional(),
});

const SimilarQuestionRequestSchema = CommonRequestSchema.extend({
  variant: SimilarQuestionVariantSchema,
});

const FillOptionsRequestSchema = CommonRequestSchema.extend({
  /** options(선택형)·answer(단답형) 배열과 같은 순서·길이의 슬롯 식별자. */
  slot_ids: z.array(z.string()),
});

const ResponseMetaSchema = z.object({
  model: z.string(),
  latency_ms: z.number().optional(),
  trace_id: z.string().optional(),
});

const SimilarQuestionResponseSchema = ResponseMetaSchema.extend({
  question: WireQuestionSchema,
  brainstorm: z.unknown().optional(),
});

const FillSchema = z.object({
  /** 요청의 slot_ids 중 하나 — 응답 대기 중 재정렬돼도 자리를 잃지 않는다. */
  slot_id: z.string(),
  text: z.string(),
  rationale: z.string().nullish(),
  kind: z.string().nullish(),
});

const FillOptionsResponseSchema = ResponseMetaSchema.extend({
  /** 채움 결과는 fills · answer_slot_ids 로만 적용한다 — 문항 자체는 읽지 않는다. */
  question: z.unknown().optional(),
  fills: z.array(FillSchema),
  /**
   * 후보 모드에서 AI 가 정답으로 채운 슬롯 id 들. 정답이 없던 요청은 지목한 자리, 정답 본문이
   * 있어도 정답으로 찍힌 빈 칸이 있던 요청은 그 빈 칸이다. 이미 내용이 있던 정답은 싣지 않는다.
   */
  answer_slot_ids: z.array(z.string()).nullish(),
});

export type SourceQuestionDto = z.infer<typeof SourceQuestionSchema>;
export type WireQuestionDto = z.infer<typeof WireQuestionSchema>;

/** 지원 유형 목록(QUESTION_CARD_AI_QUIZ_TYPES)과 응답 스키마 분기가 어긋나면 타입 에러가 난다. */
type QuizTypesMatch = [WireQuestionDto['quiz_type']] extends [
  (typeof QUESTION_CARD_AI_QUIZ_TYPES)[number],
]
  ? [(typeof QUESTION_CARD_AI_QUIZ_TYPES)[number]] extends [
      WireQuestionDto['quiz_type'],
    ]
    ? true
    : never
  : never;
const _wireCoversQuizTypes: QuizTypesMatch = true;
export type SimilarQuestionVariant = z.infer<
  typeof SimilarQuestionVariantSchema
>;
export type SimilarQuestionResponseDto = z.infer<
  typeof SimilarQuestionResponseSchema
>;
export type FillOptionsResponseDto = z.infer<typeof FillOptionsResponseSchema>;

/** 요청에 실리는 공통 입력(camelCase). 엔드포인트별 추가 필드는 `extra` 가 붙인다. */
type CommonRequest = {
  locale: string;
  question: SourceQuestionDto;
  builderContext: BuilderContextInput;
  clientRequestId: string;
  signal?: AbortSignal;
};

export type SimilarQuestionRequest = CommonRequest & {
  variant: SimilarQuestionVariant;
};

export type FillOptionsRequest = CommonRequest & {
  /** 채움 대상 배열과 같은 순서의 슬롯 id. */
  slotIds: string[];
};

type Endpoint<
  Req extends CommonRequest,
  Body extends z.ZodTypeAny,
  Res extends z.ZodTypeAny,
> = {
  path: string;
  /** 요청 본문 계약. 입구는 관대하게 두므로 런타임 검증 없이 본문 타입만 정한다. */
  request: Body;
  /** 공통 본문 외에 이 엔드포인트만 싣는 필드. */
  extra: (
    request: Req,
  ) => Omit<z.infer<Body>, keyof z.infer<typeof CommonRequestSchema>>;
  /** 응답 계약 — 런타임에 검증한다. */
  response: Res;
};

export const QUESTION_CARD_AI_ENDPOINTS = {
  similar: {
    path: '/quiz-builder/similar-question',
    request: SimilarQuestionRequestSchema,
    extra: request => ({ variant: request.variant }),
    response: SimilarQuestionResponseSchema,
  } satisfies Endpoint<
    SimilarQuestionRequest,
    typeof SimilarQuestionRequestSchema,
    typeof SimilarQuestionResponseSchema
  >,
  fill_options: {
    path: '/quiz-builder/fill-options',
    request: FillOptionsRequestSchema,
    extra: request => ({ slot_ids: request.slotIds }),
    response: FillOptionsResponseSchema,
  } satisfies Endpoint<
    FillOptionsRequest,
    typeof FillOptionsRequestSchema,
    typeof FillOptionsResponseSchema
  >,
};

export class QuestionCardAiInvalidResponseError extends Error {
  constructor(path: string, issues: z.ZodIssue[]) {
    super(
      `${path}: ${issues
        .map(issue => `${issue.path.join('.')} ${issue.message}`)
        .join('; ')}`,
    );
    this.name = 'QuestionCardAiInvalidResponseError';
  }
}

const toBuilderContextDto = (context: BuilderContextInput) =>
  context
    ? {
        school_level: context.schoolLevel,
        grade: context.grade,
        subject: context.subject,
      }
    : undefined;

/** 공통 본문 + 엔드포인트 추가 필드 → POST → 응답 스키마 검증. */
export const callQuestionCardAiEndpoint = async <
  Req extends CommonRequest,
  Body extends z.ZodTypeAny,
  Res extends z.ZodTypeAny,
>(
  endpoint: Endpoint<Req, Body, Res>,
  request: Req,
  dependencies: QuestionCardAiTransportDependencies = {},
): Promise<z.infer<Res>> => {
  const body = {
    locale: request.locale,
    question: request.question,
    builder_context: toBuilderContextDto(request.builderContext),
    client_request_id: request.clientRequestId,
    ...endpoint.extra(request),
  };
  const raw = await postQuestionCardAiJson<unknown>(
    endpoint.path,
    body,
    request.signal,
    dependencies,
  );
  const parsed = endpoint.response.safeParse(raw);
  if (!parsed.success)
    throw new QuestionCardAiInvalidResponseError(
      endpoint.path,
      parsed.error.issues,
    );
  return parsed.data;
};
